import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { Button } from '@fiado/react-kit';
import { Banner } from '../../../components/ui/Banner';
import { platformApi } from '../../../api/endpoints/platform';
import { handleApiError } from '../../../api/errorMapping';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    tenantId: string;
    /** Nombre de la organización que se muestra en el aviso. */
    tenantName: string;
    /** Nombres de las tablas DDB del silo que faltan crear (DEC-RBAC-058). */
    infraRequirements: string[];
}

/**
 * Modal post-alta cuando el tenant quedó en `pending-infra` (DEC-RBAC-058): el pool Cognito se creó pero
 * faltan tablas DDB del silo, así que el tenant NO es operativo todavía. Muestra la lista de tablas para
 * que el usuario se las pase al equipo de infraestructura + un botón "Revisar infra" que re-corre
 * `verify-infra` (DEC-RBAC-059): si las tablas ya existen, el backend siembra los roles diferidos y deja
 * el tenant `active` → cerramos con éxito; si aún faltan, refrescamos la lista y seguimos mostrando el modal.
 *
 * Usa el overlay inline (no Radix Dialog Portal) — patrón testeable del repo (ver RoleAssignmentDialog):
 * el Portal de Radix no monta en jsdom. Muestra la organización por su nombre, nunca por su id.
 */
export function PendingInfraModal({
    open,
    onOpenChange,
    tenantId,
    tenantName,
    infraRequirements,
}: Props) {
    // Lista local de tablas faltantes: arranca con la del alta y se va refrescando con cada
    // re-verificación (verify-infra devuelve la lista actualizada).
    const [missing, setMissing] = useState<string[]>(infraRequirements);
    const queryClient = useQueryClient();

    const verifyMut = useMutation({
        mutationFn: () => platformApi.verifyTenantInfra(tenantId),
        onSuccess: async (res) => {
            await queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
            if (res.status === 'active') {
                toast.success('Infraestructura lista. Organización activada.');
                onOpenChange(false);
            } else {
                setMissing(res.infraRequirements ?? []);
                toast.info('Todavía faltan tablas. Revisa la lista actualizada.');
            }
        },
        onError: handleApiError,
    });

    if (!open) return null;

    // Orden alfabético (default de presentación Fiado).
    const tables = [...missing].sort((a, b) => a.localeCompare(b, 'es'));

    return (
        <div
            role="dialog"
            aria-label="Organización pendiente de infraestructura"
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16"
        >
            <button
                type="button"
                aria-label="Cerrar"
                className="fixed inset-0 bg-black/50"
                onClick={() => {
                    onOpenChange(false);
                }}
            />
            <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">
                        Organización creada, pendiente de infraestructura
                    </h2>
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="-mr-1 rounded-md p-1.5 text-foreground/70 transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => {
                            onOpenChange(false);
                        }}
                    >
                        <X className="size-4" aria-hidden="true" />
                    </button>
                </div>

                <p className="mb-4 text-sm text-muted-foreground">
                    La organización <span className="font-medium">{tenantName}</span> quedó creada,
                    pero le faltan tablas. No estará operativa hasta que infraestructura las cree.
                </p>

                <Banner variant="warning" className="mb-4">
                    Contacta al equipo de infraestructura para que cree estos recursos.
                </Banner>

                <div className="space-y-1.5">
                    <p className="text-sm font-medium">Tablas faltantes</p>
                    {tables.length > 0 ? (
                        <ul className="space-y-1 rounded-md border bg-muted p-3">
                            {tables.map((t) => (
                                <li key={t}>
                                    <code className="select-all break-all font-mono text-sm text-foreground">
                                        {t}
                                    </code>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            No hay tablas pendientes informadas.
                        </p>
                    )}
                </div>

                <div className="mt-6 flex justify-end gap-2">
                    <Button
                        variant="outline"
                        onClick={() => {
                            onOpenChange(false);
                        }}
                    >
                        Cerrar
                    </Button>
                    <Button
                        isLoading={verifyMut.isPending}
                        onClick={() => {
                            verifyMut.mutate();
                        }}
                    >
                        Revisar infraestructura
                    </Button>
                </div>
            </div>
        </div>
    );
}
